// Drift Auditor -- strategy comparison grid (TanStack Table v8).
//
// Renders the 3 fully-computed strategies plus the 4th "PRO" row blurred behind
// an institutional upsell overlay (driven by row.is_locked_pro_tier).

import React, { useMemo } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Lock } from 'lucide-react';

import type { StrategyMetricsRow } from '../types';

const fmtMoney = (v: number | null): string =>
    v == null ? '—' : `$${Math.round(v).toLocaleString('en-US')}`;
const fmtPct = (v: number | null): string => (v == null ? '—' : `${v.toFixed(2)}%`);
const fmtNum = (v: number | null): string => (v == null ? '—' : `${v}`);

const columnHelper = createColumnHelper<StrategyMetricsRow>();

const columns = [
    columnHelper.accessor('strategy_name', {
        header: 'Strategy',
        cell: (c) => <span className="font-semibold text-slate-800">{c.getValue()}</span>,
    }),
    columnHelper.accessor('final_wealth', {
        header: 'Final Wealth',
        cell: (c) => <span className="font-bold text-slate-900">{fmtMoney(c.getValue())}</span>,
    }),
    columnHelper.accessor('cagr_pct', { header: 'CAGR', cell: (c) => fmtPct(c.getValue()) }),
    columnHelper.accessor('annualized_volatility_pct', {
        header: 'Volatility',
        cell: (c) => fmtPct(c.getValue()),
    }),
    columnHelper.accessor('max_drawdown_pct', {
        header: 'Max Drawdown',
        cell: (c) => <span className="text-rose-600 font-medium">{fmtPct(c.getValue())}</span>,
    }),
    columnHelper.accessor('sharpe_ratio', { header: 'Sharpe', cell: (c) => fmtNum(c.getValue()) }),
    columnHelper.accessor('ulcer_index', { header: 'Ulcer', cell: (c) => fmtNum(c.getValue()) }),
    columnHelper.accessor('cvar_95_pct', {
        header: 'CVaR 95%',
        cell: (c) => fmtPct(c.getValue()),
    }),
    columnHelper.accessor('variance_drain_annual_pct', {
        header: 'Variance Drain',
        cell: (c) => fmtPct(c.getValue()),
    }),
    columnHelper.accessor('rebalance_events_count', {
        header: 'Rebalances',
        cell: (c) => fmtNum(c.getValue()),
    }),
    columnHelper.accessor('annual_turnover_pct', {
        header: 'Turnover',
        cell: (c) => fmtPct(c.getValue()),
    }),
];

export const StrategyComparisonTable: React.FC<{ rows: StrategyMetricsRow[] }> = ({ rows }) => {
    const data = useMemo(() => rows, [rows]);
    const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-5 pt-5 pb-3">
                <h3 className="text-base font-bold text-slate-800">Strategy Comparison</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                    Same holdings, three rebalancing disciplines — net of transaction costs.
                </p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        {table.getHeaderGroups().map((hg) => (
                            <tr key={hg.id} className="bg-slate-50 border-y border-slate-100">
                                {hg.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap"
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            const locked = row.original.is_locked_pro_tier;
                            return (
                                <tr
                                    key={row.id}
                                    className={`border-b border-slate-50 last:border-0 ${
                                        locked ? 'relative' : 'hover:bg-slate-50/60'
                                    }`}
                                >
                                    {locked ? (
                                        <>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700">
                                                    <Lock className="h-3.5 w-3.5" />
                                                    Forge Ultra Dynamic Rebalancing (PRO)
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="font-bold text-emerald-700">
                                                    {fmtMoney(row.original.final_wealth)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap font-semibold text-emerald-700">
                                                {fmtPct(row.original.cagr_pct)}
                                            </td>
                                            <td colSpan={columns.length - 3} className="p-0">
                                                <a
                                                    href="https://www.ki-wealth.com/membership-levels"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold text-sm hover:from-indigo-500 hover:to-violet-500 transition-colors"
                                                >
                                                    <Lock className="h-4 w-4" />
                                                    Get My Professional Portfolio Review &rarr;
                                                </a>
                                            </td>
                                        </>
                                    ) : (
                                        row.getVisibleCells().map((cell) => (
                                            <td
                                                key={cell.id}
                                                className="px-4 py-3 text-slate-700 whitespace-nowrap"
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
